import {
  ReportExtractionItemCitationType,
  ReportExtractionItemType,
} from "../models/reportExtraction";

/**
 * Pick the citation on ``item`` that sourced the value in ``fieldName``.
 * Prefers per-field attribution when available; falls back to a text-match
 * heuristic against each citation's ``shortSummary`` when no citation
 * carries a ``fieldName``.
 */
export function pickCitationForField(
  item: ReportExtractionItemType,
  fieldName: string | null,
): ReportExtractionItemCitationType | null {
  if (item.citations.length === 0) return null;
  if (fieldName === null) return item.citations[0];

  const groundTruthMatches = item.citations.filter(
    (citation) => citation.fieldName === fieldName,
  );
  if (groundTruthMatches.length > 0) {
    return groundTruthMatches[0];
  }

  // If any citation on the item has a fieldName, per-field attribution IS
  // available but not for this specific field — return null so the caller
  // doesn't jump to a citation that sourced a different column.
  const anyFieldStamped = item.citations.some(
    (citation) => citation.fieldName != null,
  );
  if (anyFieldStamped) {
    return null;
  }

  const cellText = normaliseCellText(item.data[fieldName]);
  if (cellText === "") {
    return positionalFallback(item, fieldName);
  }

  let best: ReportExtractionItemCitationType | null = null;
  let bestScore = 0;

  for (const citation of item.citations) {
    const snippet = (citation.shortSummary ?? "").toLowerCase().trim();
    if (snippet === "") continue;
    const score = scoreMatch(cellText, snippet);
    if (score > bestScore) {
      bestScore = score;
      best = citation;
    }
  }

  if (best === null || bestScore === 0) {
    return positionalFallback(item, fieldName);
  }
  return best;
}

function positionalFallback(
  item: ReportExtractionItemType,
  fieldName: string,
): ReportExtractionItemCitationType {
  const fields = Object.keys(item.data);
  const fieldIndex = fields.indexOf(fieldName);
  const citationIndex =
    fieldIndex >= 0 ? fieldIndex % item.citations.length : 0;
  return item.citations[citationIndex];
}

function normaliseCellText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map((element) => String(element))
      .join(" ")
      .toLowerCase()
      .trim();
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value).toLowerCase().trim();
  }
  try {
    return JSON.stringify(value).toLowerCase().trim();
  } catch {
    return "";
  }
}

function scoreMatch(needle: string, haystack: string): number {
  // Skip trivially-short needles that would false-positive on any snippet.
  if (needle.length >= 10 && haystack.includes(needle)) {
    return 1000 + needle.length;
  }
  // Snippet window is ~200 chars, so long fields may only have their
  // first chunk quoted.
  if (needle.length >= 40) {
    const prefix = needle.slice(0, 40);
    if (haystack.includes(prefix)) return 500 + prefix.length;
  }
  const needleWords = needle.split(/\s+/).filter((w) => w.length > 4);
  if (needleWords.length === 0) return 0;
  const haystackWords = new Set(
    haystack.split(/\s+/).filter((w) => w.length > 4),
  );
  let common = 0;
  for (const word of needleWords) {
    if (haystackWords.has(word)) common++;
  }
  return common;
}
